import { ReportMetadata, SubstitutionConfig } from '../types';
import { parseDateLocal, formatDateLocal, getStartOfWeek, addDays, getCalendarWeek } from './dateService';

/**
 * Urlaubsvertretungen: pure Lese-/Rechenlogik über `metadata.substitutions[]`
 * mit Fallback auf das Legacy-Feld `metadata.substitutionConfig`.
 * Quelle der Wahrheit ist das Array; das Legacy-Feld wird beim Schreiben
 * per `mirrorLegacyConfig` für alte Clients gespiegelt.
 */

export interface WeekSlot {
    /** Geclampter Wochenanfang (YYYY-MM-DD, nie vor Zeitraumbeginn). */
    weekStart: string;
    /** Geclamptes Wochenende (YYYY-MM-DD, nie nach Zeitraumende). */
    weekEnd: string;
    /** ISO-Kalenderwoche des Montags dieser Woche. */
    kw: number;
    /** ISO-Jahr der Kalenderwoche (Jahreswechsel!). */
    year: number;
}

const hasValidRange = (e: SubstitutionConfig | undefined | null): boolean =>
    !!e && !!parseDateLocal(e.startDate) && !!parseDateLocal(e.endDate);

const entryKey = (e: SubstitutionConfig): string =>
    `${(e.substituteEmail || '').trim().toLowerCase()}|${e.startDate}|${e.endDate}`;

export const getSubstitutions = (meta: ReportMetadata | undefined | null): SubstitutionConfig[] => {
    if (!meta) return [];
    // Array ist Quelle der Wahrheit; das Legacy-Feld wird trotzdem gemergt,
    // weil ein alter Client parallel NUR substitutionConfig geschrieben haben kann.
    // Bei identischem Schlüssel (E-Mail+Zeitraum) gewinnt der Array-Eintrag.
    // INVARIANTE: Stornieren setzt active:false, entfernt aber nie Einträge —
    // diese Tombstones verhindern, dass ein veraltetes Legacy-Feld einen
    // stornierten Eintrag über den Merge wiederbelebt. Nicht hart löschen!
    const list: SubstitutionConfig[] = Array.isArray(meta.substitutions) ? [...meta.substitutions] : [];
    if (meta.substitutionConfig) list.push(meta.substitutionConfig);
    const seen = new Set<string>();
    return list
        .filter(hasValidRange)
        .filter(e => {
            const k = entryKey(e);
            if (seen.has(k)) return false;
            seen.add(k);
            return true;
        })
        .sort((a, b) => a.startDate.localeCompare(b.startDate));
};

const coversDate = (e: SubstitutionConfig, date: Date): boolean => {
    if (!e.active) return false;
    const s = parseDateLocal(e.startDate);
    const end = parseDateLocal(e.endDate);
    if (!s || !end) return false;
    const d = new Date(date); d.setHours(12, 0, 0, 0);
    s.setHours(0, 0, 0, 0);
    end.setHours(23, 59, 59, 999);
    return d >= s && d <= end;
};

export const getActiveSubstitution = (
    meta: ReportMetadata | undefined | null,
    date: Date = new Date()
): SubstitutionConfig | null =>
    getSubstitutions(meta).find(e => coversDate(e, date)) || null;

/** Aktiver Eintrag, der genau diesen Vertreter (E-Mail) abdeckt. */
export const getActiveSubstitutionFor = (
    meta: ReportMetadata | undefined | null,
    email: string | undefined | null,
    date: Date = new Date()
): SubstitutionConfig | null => {
    const mail = (email || '').trim().toLowerCase();
    if (mail.length <= 3) return null;
    return getSubstitutions(meta).find(e =>
        coversDate(e, date) && (e.substituteEmail || '').trim().toLowerCase() === mail
    ) || null;
};

export const isUserActiveSubstitute = (
    meta: ReportMetadata | undefined | null,
    email: string | undefined | null,
    date: Date = new Date()
): boolean => !!getActiveSubstitutionFor(meta, email, date);

/** Inklusive Überlappung zweier Tagesbereiche; Randberührung zählt als Überlappung. */
export const hasOverlap = (
    entries: SubstitutionConfig[],
    startDate: string,
    endDate: string,
    ignore?: (e: SubstitutionConfig) => boolean
): boolean => {
    const s = parseDateLocal(startDate);
    const e = parseDateLocal(endDate);
    if (!s || !e) return false;
    return entries.some(entry => {
        if (!entry.active || (ignore && ignore(entry))) return false;
        const es = parseDateLocal(entry.startDate);
        const ee = parseDateLocal(entry.endDate);
        if (!es || !ee) return false;
        return es <= e && s <= ee;
    });
};

/** Kalenderwochen (Mo–So) im Zeitraum, Ränder auf den Zeitraum geclampt. */
export const weeksInRange = (startDate: string, endDate: string): WeekSlot[] => {
    const start = parseDateLocal(startDate);
    const end = parseDateLocal(endDate);
    if (!start || !end || start > end) return [];

    const slots: WeekSlot[] = [];
    let monday = getStartOfWeek(start);
    // Sicherung gegen Endlosschleife bei kaputten Daten: max. 2 Jahre.
    for (let i = 0; i < 106 && monday <= end; i++) {
        const sunday = addDays(monday, 6);
        const clampedStart = monday < start ? start : monday;
        const clampedEnd = sunday > end ? end : sunday;
        slots.push({
            weekStart: formatDateLocal(clampedStart),
            weekEnd: formatDateLocal(clampedEnd),
            kw: getCalendarWeek(monday),
            // ISO-Jahr: das Jahr des Donnerstags der Woche
            year: addDays(monday, 3).getFullYear(),
        });
        monday = addDays(monday, 7);
    }
    return slots;
};

/**
 * Ein zu vertretender Abschnitt einer Woche. Hat ein Projekt mehrere
 * Begehungen pro Woche, bekommt jede ihren eigenen Abschnitt — so kann jede
 * an einen anderen Kollegen gehen.
 */
export interface CoverageSlot {
    week: WeekSlot;
    /** Abschnittsbeginn (YYYY-MM-DD). Die Abschnitte einer Woche schließen lückenlos aneinander an. */
    startDate: string;
    /** Abschnittsende (YYYY-MM-DD). */
    endDate: string;
    /** Begehungstermin im Abschnitt; null, wenn die Woche keinen hat. */
    dueDate: string | null;
    /** Wochentag des Termins (0 = So … 6 = Sa); null = ganze Woche, ein Chip wie bisher. */
    weekday: number | null;
}

/**
 * Teilt die Wochen eines Zeitraums in Vertretungsabschnitte, einen je
 * Begehungstermin. Abschnitt k reicht von Termin k bis zum Tag vor Termin
 * k+1; der erste beginnt am Wochenanfang, der letzte endet am Wochenende —
 * die Woche bleibt lückenlos abgedeckt, auch wenn ein Termin sich verschiebt.
 *
 * Hat das Projekt in KEINER Woche mehr als einen Termin, bleibt jede Woche
 * ein Abschnitt ohne Wochentag (bisheriges Verhalten: ein Chip je Projekt).
 * Sonst trägt jeder Abschnitt den Wochentag seines Termins. Eine Woche ohne
 * Termin entfällt dann ganz: Dort ist keine Begehung fällig, also auch keine
 * Vertretung (typisch eine angeschnittene Randwoche, z. B. Urlaub ab Freitag).
 *
 * `dueDates` kommen aus `substituteDueDates` — dieselben Termine, die
 * Vertretungsmail und ICS ankündigen.
 */
export const coverageSlots = (weeks: WeekSlot[], dueDates: string[]): CoverageSlot[] => {
    const sorted = Array.from(new Set(dueDates.filter(d => !!parseDateLocal(d)))).sort();
    const perWeek = weeks.map(w => sorted.filter(d => d >= w.weekStart && d <= w.weekEnd));
    const multi = perWeek.some(d => d.length > 1);
    return weeks.flatMap((week, i): CoverageSlot[] => {
        const dues = perWeek[i];
        if (multi && dues.length === 0) return [];
        if (!multi) {
            return [{ week, startDate: week.weekStart, endDate: week.weekEnd, dueDate: dues[0] ?? null, weekday: null }];
        }
        return dues.map((due, k) => ({
            week,
            startDate: k === 0 ? week.weekStart : due,
            endDate: k === dues.length - 1 ? week.weekEnd : formatDateLocal(addDays(parseDateLocal(dues[k + 1])!, -1)),
            dueDate: due,
            weekday: parseDateLocal(due)!.getDay(),
        }));
    });
};

const normDate = (d: string): string => formatDateLocal(parseDateLocal(d));
const shiftDate = (d: string, days: number): string => formatDateLocal(addDays(parseDateLocal(d)!, days));

/**
 * Abschnitte ohne aktive Vertretung.
 *
 * Ohne Termin zählt Teilabdeckung als abgedeckt (bisheriges Verhalten). Mit
 * Termin entscheidet der Termin selbst: Ist er noch frei, bleibt der
 * Abschnitt offen — gekürzt auf die freie Spanne um den Termin, damit das
 * Speichern nicht an der Teilvertretung (z. B. nur Montag) scheitert.
 */
export const uncoveredSlots = (
    meta: ReportMetadata | undefined | null,
    slots: CoverageSlot[]
): CoverageSlot[] => {
    const entries = getSubstitutions(meta).filter(e => e.active);
    return slots.flatMap((s): CoverageSlot[] => {
        const hits = entries.filter(e => hasOverlap([e], s.startDate, s.endDate));
        if (hits.length === 0) return [s];
        if (!s.dueDate) return [];
        const due = s.dueDate;
        if (hits.some(e => normDate(e.startDate) <= due && due <= normDate(e.endDate))) return [];
        let startDate = s.startDate;
        let endDate = s.endDate;
        hits.forEach(e => {
            const eStart = normDate(e.startDate);
            const eEnd = normDate(e.endDate);
            if (eEnd < due) {
                const after = shiftDate(eEnd, 1);
                if (after > startDate) startDate = after;
            } else if (eStart > due) {
                const before = shiftDate(eStart, -1);
                if (before < endDate) endDate = before;
            }
        });
        return [{ ...s, startDate, endDate }];
    });
};

/**
 * Fasst lückenlos aneinanderstoßende Zeiträume zusammen (Ende + 1 Tag =
 * Beginn). Übernimmt ein Kollege alle Termine einer Woche, entsteht so EIN
 * Eintrag über die Woche statt einer je Termin.
 */
export const mergeAdjacentRanges = <T extends { startDate: string; endDate: string }>(ranges: T[]): T[] => {
    const sorted = [...ranges].sort((a, b) => a.startDate.localeCompare(b.startDate));
    const out: T[] = [];
    sorted.forEach(r => {
        const last = out[out.length - 1];
        const lastEnd = last ? parseDateLocal(last.endDate) : null;
        if (last && lastEnd && formatDateLocal(addDays(lastEnd, 1)) === r.startDate) {
            out[out.length - 1] = { ...last, endDate: r.endDate > last.endDate ? r.endDate : last.endDate };
        } else {
            out.push(r);
        }
    });
    return out;
};

/**
 * Eintrag fürs Legacy-Feld `substitutionConfig`: der heute aktive, sonst der
 * nächste zukünftige, sonst ein inaktiver Marker. Hält alte Clients lesbar.
 */
export const mirrorLegacyConfig = (
    entries: SubstitutionConfig[],
    today: Date = new Date()
): SubstitutionConfig => {
    const active = entries.find(e => coversDate(e, today));
    if (active) return active;

    const t = new Date(today); t.setHours(12, 0, 0, 0);
    const upcoming = entries
        .filter(e => {
            if (!e.active) return false;
            const s = parseDateLocal(e.startDate);
            return !!s && s > t;
        })
        .sort((a, b) => a.startDate.localeCompare(b.startDate))[0];
    if (upcoming) return upcoming;

    return { active: false, startDate: '', endDate: '' };
};
